import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { Categories } from "./Categories";
import { AppContext } from "../context/AppContextProvider";
import { DocCard } from "./DocCard";
import { getDocs } from "../lib/api/api";
import { ActionTypes } from "../actions/actions";
import Loader from "./Loader";
import { Trans, useTranslation } from "react-i18next";
import Banner from "./Banner";
import { I18N_LOCALE_TO_DOCUMENT_LOCALE, IDocument } from "../@types/document";
import ErrorBlock from "./ErrorBlock";
import DocsList from "./DocsList";
import { env } from "../lib/env";

export default function DocsContainer() {
    const { state, dispatch } = useContext(AppContext)
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const { docs, product, selectedCategory } = state;
    const { seriale } = product;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const [hasMore, setHasMore] = useState(false);

    const filteredDocs = filterDocs(docs.items);

    const loadDocs = async () => {
        try {
            if (!seriale || isLoading || error) {
                return;
            }

            setIsLoading(true);

            const res = await getDocs(seriale, docs.items.length);

            if (res.error) {
                throw new Error();
            }

            const newDocs: IDocument[] = res.data.map((doc: IDocument) => {
                if (doc.deleted) return;

                return ({
                    ...doc,
                    revisions: doc.revisions.map(revision => ({
                        ...revision,
                        internalRevisionDate: new Date(revision.internalRevisionDate),
                        validityStartDate: new Date(revision.validityStartDate),
                        validityEndDate: new Date(revision.validityEndDate),
                        fileUrl: `${env.REACT_APP_BASE_FILE_URL}/${revision.fileLocation}`,
                        thumbUrl: `${env.REACT_APP_BASE_THUMBNAIL_URL}/thumbnails/${revision.fileLocation.replace(".pdf", ".png")}`
                    }))
                })
            });

            if (res.paging.next) {
                setHasMore(true);
            } else {
                setHasMore(false);
            }

            // Create the updated docs array
            const updatedDocs = [...docs.items, ...newDocs];

            // Also update context for other components that might need it
            dispatch({
                type: ActionTypes.SetDocs,
                payload: updatedDocs
            });

        } catch (error) {
            console.error("Error while loading docs:", error);
            setError(true);
        }

        setIsLoading(false);
    }

    useEffect(() => {
        loadDocs();
    }, [seriale]);

    function filterDocs(docs: IDocument[]) {
        if (!selectedCategory) return docs;

        const filteredDocsByCategory = docs.filter(doc => doc.categories.includes(selectedCategory));

        const sortedDocsByCode = filteredDocsByCategory.sort((a, b) => {
            if (a.code && !b.code) return -1;
            if (!a.code && b.code) return 1;
            if (a.code && b.code) {
                if (product.manuali.includes(a.code) && !product.manuali.includes(b.code)) return -1;
                if (!product.manuali.includes(a.code) && product.manuali.includes(b.code)) return 1;
            }
            return 0;
        });

        return sortedDocsByCode;
    }

    if (isLoading && docs.items.length === 0) {
        return <Loader />;
    }

    if (error) {
        return <LoadDocsError />
    }

    if (docs.items.length === 0) {
        return <Banner text={t("docs.noDocs")} />
    }

    return (
        <>
            {filteredDocs.length > 0 ?
                <>
                    <Categories />
                    <DocsList
                        docs={filteredDocs}
                        isLoadingDocs={isLoading}
                        onLoadDocs={loadDocs}
                        hasMore={hasMore}
                    />
                </> :
                <Banner text={t("docs.noDocsInLang")} />
            }
        </>
    );
}

const LoadDocsError = () => {
    const { t } = useTranslation();

    return (<ErrorBlock
        title={t("error.loadDocs.title")}
        description={<>
            <p>
                <Trans
                    i18nKey={"error.loadDocs.part1" as any} />
                <br />
                <br />
                <Trans i18nKey={"error.loadDocs.part2" as any} />
            </p>
            <br />
            <ul className="w-fit mx-auto list-disc">
                <li>
                    <Trans i18nKey={"error.loadDocs.part3" as any} />
                </li>
                <li>
                    <Trans i18nKey={"error.loadDocs.part4" as any} />
                </li>
            </ul>
        </>}
    />);
}
